import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-file-unknow-line text-5xl text-foreground-300 mb-4 block" />
            <p className="text-foreground-500 mb-4">文章未找到</p>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 font-medium">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    image: article.cover,
    publisher: {
      '@type': 'Organization',
      name: '向日葵视频',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.xiangrikui-video.com/' },
      { '@type': 'ListItem', position: 2, name: '文章', item: 'https://www.xiangrikui-video.com/category/all' },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <SEO
      title={article.title}
      description={article.excerpt}
      keywords={`向日葵视频,${article.tags.join(',')},文章`}
      canonicalUrl={`https://www.xiangrikui-video.com/article/${article.id}`}
      ogType="article"
      ogImage={article.cover}
      schemaData={{ ...articleSchema, ...breadcrumbSchema }}
    >
      <ScrollToTop />
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1">
          <div className="bg-background-100">
            <div className="container-main">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '文章', href: '/category/all' },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <article className="container-main py-8">
            <div className="max-w-[800px] mx-auto">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs text-accent-500 bg-accent-100 px-3 py-1 rounded-full">{article.category}</span>
                  <span className="text-xs text-foreground-400">{article.readTime}</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 leading-tight mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500">
                  <span><i className="ri-user-line mr-1" />{article.author}</span>
                  <span><i className="ri-calendar-line mr-1" />{article.date}</span>
                </div>
              </div>

              <div className="aspect-[16/9] rounded-xl overflow-hidden mb-8">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={`${article.title} - 向日葵视频`}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose prose-sm max-w-none text-foreground-700 leading-relaxed
                  [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-8 [&_h2]:mb-4
                  [&_p]:mb-4 [&_p]:leading-relaxed
                  [&_strong]:text-foreground-900"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-8 pt-6 border-t border-background-200">
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="py-12 bg-background-100">
              <div className="container-main">
                <h2 className="text-xl font-bold text-foreground-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                  相关文章
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.id} article={ra} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </SEO>
  );
}